













































































import { Facet, Searchable } from '~/models/search/types'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import CSlider from '~/components/shared/configurable/slider/CSlider.vue'
import CInput from '~/components/shared/configurable/form/input/CInput.vue'
import { debounce } from '~/utils/function'
import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch
} from '~/utils/nuxt3-migration'
import { useUserAgent } from '~/compositions/user-agent'
import { useNamespacedStore } from '~/compositions/store'
import { formatNumber } from '~/utils/number'
import { useDeps } from '~/compositions/dependency-container'
import SearchService from '~/services/search/SearchService'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'

export default defineComponent({
  components: {
    CSlider
  },
  props: {
    financeOptionsFacet: {
      type: Object as PropType<Facet>,
      required: true
    },
    discountFacet: {
      type: Object as PropType<Facet>,
      default: null
    }
  },
  setup(props, { emit }) {
    const [searchService] = useDeps(SearchService)
    const { state: classifiedSearchState } = useNamespacedStore<SearchState>(
      CLASSIFIED_SEARCH_NS
    )
    const { isPc } = useUserAgent()
    const { t } = useI18n()

    const installmentFrom = ref(
      props.financeOptionsFacet.values.selected['installment-from']
    )
    const installmentTo = ref(
      props.financeOptionsFacet.values.selected['installment-to']
    )
    const installments = ref(
      props.financeOptionsFacet.values.selected.installments ||
        props.financeOptionsFacet.values.bounds.installments.default
    )
    const downpayment = ref(
      props.financeOptionsFacet.values.selected.downpayment
    )
    const hasSomeFieldChanged = ref(false)
    const loadingCount = ref(false)
    const resultsCount = ref(classifiedSearchState.stats.total)
    const downpaymentInputRef = ref<typeof CInput>()
    const downpaymentError = ref<string>('')
    const installmentsError = ref<string>('')
    const isWithDiscountsOnlySelected = ref(
      props.discountFacet?.isSelected || false
    )
    const maxInstallments =
      props.financeOptionsFacet.values.bounds.installments.max
    const minInstallments =
      props.financeOptionsFacet.values.bounds.installments.min

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const totalStatsResults = computed(() => classifiedSearchState.stats.total)

    const installmentsFromOptions = computed(() =>
      props.financeOptionsFacet.searchables.installments.map(
        (searchable: Searchable) => ({
          value: searchable.v,
          label: searchable.n,
          disabled:
            installmentTo.value !== null &&
            parseFloat(searchable.v) > parseFloat(installmentTo.value)
        })
      )
    )

    const installmentsToOptions = computed(() =>
      props.financeOptionsFacet.searchables.installments.map(
        (searchable: Searchable) => ({
          value: searchable.v,
          label: searchable.n,
          disabled:
            installmentFrom.value !== null &&
            parseFloat(searchable.v) < parseFloat(installmentFrom.value)
        })
      )
    )

    watch(
      () => props.financeOptionsFacet,
      newFacet => {
        installmentFrom.value = newFacet?.values.selected['installment-from']
        installmentTo.value = newFacet?.values.selected['installment-to']
        installments.value =
          newFacet?.values.selected.installments ||
          newFacet?.values.bounds.installments.default
        downpayment.value = newFacet?.values.selected.downpayment
      }
    )

    watch(totalStatsResults, (newTotal: number) => {
      resultsCount.value = newTotal
    })

    watch(
      () => props.discountFacet,
      (newValue: Facet) => {
        isWithDiscountsOnlySelected.value = newValue?.isSelected
      }
    )

    function onShowButtonClick() {
      validateFields()
      if (!installmentsError.value && !downpaymentError.value) {
        hasSomeFieldChanged.value = false
        emitValues()
      }
    }

    function onFieldChange() {
      if (installmentsError.value || downpaymentError.value) {
        return
      }

      // The data is only emitted when the user clicks the "Show" button on desktop
      if (isPc.value) {
        loadClassifiedsCount()
      } else {
        emitValues()
      }
    }

    async function loadClassifiedsCount() {
      loadingCount.value = true

      const data = await searchService.getFacets({
        ...classifiedSearchState.config.rootParams,
        ...classifiedSearchState.params,
        ...{
          'installment-from': installmentFrom.value,
          'installment-to': installmentTo.value,
          installments: installments.value,
          downpayment: downpayment.value
        }
      })

      resultsCount.value = data.stats.total
      loadingCount.value = false
    }

    function emitValues() {
      emit('change', {
        installmentFrom: installmentFrom.value,
        installmentTo: installmentTo.value,
        installments: installments.value,
        downpayment: downpayment.value
      })
    }

    function validateFields() {
      downpaymentError.value = downpayment.value
        ? ''
        : t('enter_downpayment_amount')
      installmentsError.value =
        installmentFrom.value || installmentTo.value
          ? ''
          : t('select_monthly_installment')
    }

    function onNumberOfInstallmentsChange(value: number) {
      // Manually mutating the value here because CSlider v-model is not synchronized when the lazy prop is set to true
      installments.value = value
      hasSomeFieldChanged.value = true
      validateFields()
      onFieldChange()
    }

    function onInstallmentAmountChange() {
      hasSomeFieldChanged.value = true
      validateFields()
      onFieldChange()
    }

    function onDownpaymentInput() {
      hasSomeFieldChanged.value = true
      loadingCount.value = true
      debouncedDownpaymentFieldChange()
    }

    function onDiscountsOnlyChange(value: boolean) {
      emit('discounts-only-change', value)
    }

    const debouncedDownpaymentFieldChange = debounce(() => {
      validateFields()
      onFieldChange()
    }, 1000)

    return {
      isPc,
      downpayment,
      downpaymentInputRef,
      downpaymentError,
      installmentsFromOptions,
      installmentsToOptions,
      installmentFrom,
      installmentTo,
      installments,
      installmentsError,
      loadingCount,
      loadingFacets,
      resultsCount,
      isWithDiscountsOnlySelected,
      hasSomeFieldChanged,
      maxInstallments,
      minInstallments,
      formatNumber,
      onShowButtonClick,
      onFieldChange,
      onInstallmentAmountChange,
      onNumberOfInstallmentsChange,
      onDownpaymentInput,
      onDiscountsOnlyChange
    }
  },
  i18n: defineComponentTranslations({
    select_monthly_installment: {
      en: 'Select monthly installment',
      el: 'Επίλεξε μηνιαία δόση'
    },
    enter_downpayment_amount: {
      en: 'Enter downpayment amount',
      el: 'Επίλεξε ποσό προκαταβολής'
    }
  })
})
